"use client";

import { FileImage, FileSpreadsheet, FileText, Trash2 } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { BriefAttachment, briefAttachmentHref, cn } from "@drpcrd/utils";
import { Button } from "../buttons/button";
import { CustomSpinner } from "../icons/custom-spinner";

type Props = {
  attachments: BriefAttachment[];
  /** Names of files still uploading, listed after the attached ones. */
  pending?: string[];
  /** When given, every attached file gets a remove button. */
  onRemove?: (attachment: BriefAttachment) => void;
  className?: string;
};

const attachmentIcon = (name: string): LucideIcon => {
  const extension = name.split(".").pop()?.toLowerCase();
  if (
    extension === "jpg" ||
    extension === "jpeg" ||
    extension === "png" ||
    extension === "gif"
  ) {
    return FileImage;
  }
  if (extension === "xls" || extension === "xlsx" || extension === "csv") {
    return FileSpreadsheet;
  }
  return FileText;
};

/**
 * Files attached to a brief, one row each: a type icon, the name as a link
 * that opens the file, and (while editing) a remove button. Files still
 * uploading are listed underneath with a spinner. Renders nothing when there
 * is nothing to show. The link is a plain anchor so a Next `Link` does not
 * prefetch the private files route on hover.
 */
export const BriefAttachmentList = ({
  attachments,
  pending = [],
  onRemove,
  className,
}: Props) => {
  if (attachments.length === 0 && pending.length === 0) {
    return null;
  }

  return (
    <ul className={cn("flex flex-col gap-1", className)}>
      {attachments.map((attachment) => {
        const Icon = attachmentIcon(attachment.name);
        return (
          <li
            key={attachment.id}
            className="flex items-center justify-between gap-2"
          >
            <span className="flex min-w-0 items-center gap-2">
              <Icon className="text-primary shrink-0" size={24} aria-hidden />
              <a
                href={briefAttachmentHref(attachment)}
                target="_blank"
                rel="noreferrer"
                className="text-primary truncate text-sm font-semibold hover:underline"
              >
                {attachment.name}
              </a>
            </span>
            {onRemove ? (
              <Button
                type="button"
                variant="outlined"
                icon={Trash2}
                className="size-4 shrink-0 p-0"
                aria-label={`Remove ${attachment.name}`}
                onClick={() => onRemove(attachment)}
              />
            ) : null}
          </li>
        );
      })}
      {pending.map((name, index) => (
        <li
          key={`${name}-${index}`}
          className="flex items-center gap-2 text-sm text-neutral-900"
        >
          <CustomSpinner className="size-2 shrink-0" aria-hidden />
          <span className="truncate">Uploading {name}…</span>
        </li>
      ))}
    </ul>
  );
};
